/**
 * /inbox — what waits for the person (docs/refactor/03-caixa-de-entrada.md): work that needs them,
 * failed, or ended, and commands waiting for their computer. Opening an item takes them where it is
 * and marks it read; "Arquivar" puts it away. One list, newest first within each kind.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useState } from 'react'
import { Globe, Users, ListTodo, Archive, Bell, CheckCheck, CircleAlert, CircleCheck, CircleDollarSign, Clock, Hand, Square, UserPlus } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { formatRelative } from '../lib/format'
import { canAskForNotifications } from '../lib/inboxNotify'
import { needsHomeScreen, subscribeToPush } from '../lib/push'
import type { InboxItem } from '../lib/types'
import { EmptyState, Panel, Skeleton } from '../components/primitives'
import { InboxAnswer } from '../components/InboxAnswer'

const ORDER: InboxItem['kind'][] = ['invite', 'needsYou', 'spend', 'published', 'team', 'pending', 'failed', 'done', 'waiting']
const ICONS: Record<InboxItem['kind'], JSX.Element> = {
  invite: <UserPlus size={15} />,
  spend: <CircleDollarSign size={15} />,
  needsYou: <Hand size={15} />,
  failed: <CircleAlert size={15} />,
  done: <CircleCheck size={15} />,
  waiting: <Clock size={15} />,
  team: <Users size={15} />,
  pending: <ListTodo size={15} />,
  published: <Globe size={15} />,
}

/** A team item's detail is "builds|From", "reviews|From", "runs|From" or "loops|From": said in words. */
function detailOf(item: InboxItem, t: (key: string, vars?: Record<string, string | number>) => string): string {
  // One's own order that the teammate it was sent to stopped (docs/refactor/63, M2): "stopped_by_recipient|Who".
  if (item.key.startsWith('order:') && item.detail?.startsWith('stopped_by_recipient|')) return t('inbox.order.stoppedBy', { from: item.detail.slice('stopped_by_recipient|'.length) })
  // A pending item's detail is the part of the solution it belongs to (the list's ## section).
  if (item.kind === 'pending') return item.detail ? t('inbox.pending.in', { section: item.detail }) : ''
  if (item.kind === 'invite') return t('inbox.invite.from', { from: item.detail ?? t('inbox.invite.someone') })
  if (item.kind === 'published') return item.detail ? t('inbox.published.at', { url: item.detail.replace('https://', '') }) : ''
  // A spending limit (51, §6.3): "spent|alert|stop|stopped or the period".
  if (item.kind === 'spend' && item.detail) {
    const [spent = '', alert = '', stop = '', state = ''] = item.detail.split('|')
    if (state === 'stopped') return t('inbox.spend.stopped', { spent, stop })
    return stop ? t('inbox.spend.alertStop', { spent, alert, stop }) : t('inbox.spend.alert', { spent, alert })
  }
  if (item.kind !== 'team' || !item.detail) return item.detail ?? ''
  const [role, from] = item.detail.split('|')
  const key = role === 'builds' || role === 'reviews' || role === 'loops' ? `inbox.team.${role}` : 'inbox.team.runs'
  return t(key, { from: from ?? '' })
}

export default function InboxPage() {
  const { t, locale } = useI18n()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['inbox'], queryFn: api.inbox.list, refetchInterval: 60_000 })
  const [askable, setAskable] = useState(canAskForNotifications)
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['inbox'] })
  const read = useMutation({ mutationFn: (keys: string[]) => api.inbox.read(keys), onSuccess: refresh })
  const archive = useMutation({ mutationFn: (keys: string[]) => api.inbox.archive(keys), onSuccess: refresh })
  // An invitation to a team or a project is answered here; nobody joins without it (docs/refactor/55).
  const answerInvite = useMutation({
    mutationFn: ({ key, accept }: { key: string; accept: boolean }) => api.inbox.answerInvite(key, accept),
    onSuccess: () => {
      refresh()
      void queryClient.invalidateQueries({ queryKey: ['teams'] })
    },
  })
  // A teammate's order this person's agent would do is stopped here, whole: nobody's computer keeps doing work they no
  // longer want, and the sender is told who stopped it (docs/refactor/63, M2).
  const stopOrder = useMutation({
    mutationFn: (key: string) => api.workOrders.stop(key.replace(/^team:/, '')),
    onSuccess: () => {
      refresh()
      void queryClient.invalidateQueries({ queryKey: ['work-orders'] })
      void queryClient.invalidateQueries({ queryKey: ['prompt-queue'] })
    },
  })

  // A notification's "Aprovar" or "Responder" opens here: ?item=prompt:…&do=approve|reply.
  const [params] = useSearchParams()
  const action = params.get('do')
  const asked: { item: string; action: 'approve' | 'reply' } | null =
    params.get('item') && (action === 'approve' || action === 'reply') ? { item: params.get('item')!, action } : null

  const items = data?.items ?? []
  const unread = items.filter((i) => !i.read).map((i) => i.key)

  const open = (item: InboxItem) => {
    if (!item.read) read.mutate([item.key])
    navigate(item.link)
  }

  return (
    <div className="stack inbox">
      <div className="row row--between row--wrap">
        <p className="small muted" style={{ margin: 0 }}>{t('inbox.lead')}</p>
        <div className="row row--tight">
          {askable && (
            <button type="button" className="btn btn--sm" onClick={() => void Notification.requestPermission().then(() => { setAskable(canAskForNotifications()); void subscribeToPush() })}>
              <Bell size={13} /> {t('inbox.allowNotifications')}
            </button>
          )}
          <button type="button" className="btn btn--sm btn--ghost" onClick={() => read.mutate(unread)} disabled={unread.length === 0 || read.isPending}>
            <CheckCheck size={13} /> {t('inbox.readAll')}
          </button>
        </div>
      </div>

      {/* An iPhone gets notifications only from the site on the home screen (Apple's rule): say how. */}
      {needsHomeScreen() && <p className="tiny muted" style={{ margin: 0 }}>{t('inbox.iosHomeScreen')}</p>}

      {isLoading && <Skeleton height={120} />}
      {!isLoading && items.length === 0 && <EmptyState title={t('inbox.empty')} body={t('inbox.emptyHint')} />}

      {ORDER.map((kind) => {
        const group = items.filter((i) => i.kind === kind)
        if (group.length === 0) return null
        return (
          <Panel key={kind} title={`${t(`inbox.kind.${kind}`)} · ${group.length}`}>
            <ul className="inbox__list">
              {group.map((item) => (
                <li key={item.key} className={`inbox__item inbox__item--${item.kind}${item.read ? '' : ' is-unread'}`}>
                  <span className="inbox__icon" aria-hidden>{ICONS[item.kind]}</span>
                  <button type="button" className="inbox__main" onClick={() => open(item)}>
                    <span className="inbox__title small">
                      {!item.read && <span className="inbox__dot" aria-label={t('inbox.unread')} />}
                      {item.title}
                    </span>
                    {item.detail && <span className="inbox__detail tiny muted">{detailOf(item, t)}</span>}
                    <span className="tiny muted">
                      {item.repository ? `${item.repository} · ` : ''}
                      {formatRelative(item.at, locale)}
                    </span>
                  </button>
                  <button type="button" className="btn btn--ghost btn--icon btn--sm" onClick={() => archive.mutate([item.key])} title={t('inbox.archive')} aria-label={t('inbox.archive')}>
                    <Archive size={14} />
                  </button>
                  {/* An agent that stopped to ask, answered here or from the notification (51, §6.4). */}
                  {item.kind === 'invite' && (
                    <div className="row row--tight inbox__invite">
                      <button type="button" className="btn btn--sm btn--primary" disabled={answerInvite.isPending} onClick={() => answerInvite.mutate({ key: item.key, accept: true })}>
                        {t('inbox.invite.accept')}
                      </button>
                      <button type="button" className="btn btn--sm btn--ghost" disabled={answerInvite.isPending} onClick={() => answerInvite.mutate({ key: item.key, accept: false })}>
                        {t('inbox.invite.decline')}
                      </button>
                    </div>
                  )}
                  {item.kind === 'needsYou' && item.key.startsWith('prompt:') && (
                    <InboxAnswer itemKey={item.key} start={asked?.item === item.key ? asked.action : null} />
                  )}
                  {item.kind === 'team' && (
                    <button type="button" className="btn btn--sm btn--ghost" disabled={stopOrder.isPending} onClick={() => stopOrder.mutate(item.key)} title={t('inbox.team.stopHint')}>
                      <Square size={12} /> {t('inbox.team.stop')}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </Panel>
        )
      })}
    </div>
  )
}
